import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  Tab,
  Tabs,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getStocktakeDetail, transitionStocktake } from '../../collections/stocktakes';
import Stocktake from '../../models/stocktake';
import StocktakeItem from '../../models/stocktakeItem';

const StocktakeDetail = (props) => {
  const { stocktakeId } = useParams();
  const navigate = useNavigate();
  
  const [stocktake, setStocktake] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  
  const userRole = props.controlRole;

  useEffect(() => {
    props.setPageTitle("Chi tiết phiếu kiểm kho");
    fetchStocktakeData();
  }, [stocktakeId]);

  const fetchStocktakeData = async () => {
    try {
      setLoading(true);
      const response = await getStocktakeDetail(stocktakeId);
      
      if (response && response.status === 200) {
        const stocktakeData = response.data;
        const stocktakeModel = new Stocktake();
        stocktakeModel.mapData(stocktakeData);
        setStocktake(stocktakeModel);
        
        // Map items to StocktakeItem models
        const stocktakeItems = (stocktakeData.items || []).map(item => {
          const stocktakeItem = new StocktakeItem();
          stocktakeItem.mapData(item);
          return stocktakeItem;
        });
        setItems(stocktakeItems);
      }
    } catch (error) {
      console.error('Error fetching stocktake:', error);
      toast.error('Lỗi tải dữ liệu kiểm kho');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      setActionLoading(true);
      const response = await transitionStocktake(stocktakeId, action);
      
      if (response && response.status === 200) {
        toast.success(`${action === 'submit' ? 'Đã gửi duyệt' : action === 'approve' ? 'Đã duyệt' : 'Đã từ chối'} phiếu kiểm kho`);
        await fetchStocktakeData();
      }
    } catch (error) {
      console.error(`Error ${action} stocktake:`, error);
      toast.error(error.response?.data?.error || `Lỗi ${action} phiếu kiểm kho`);
    } finally {
      setActionLoading(false);
    }
  };

  const getVarianceItems = () => {
    return items.filter(item => item.is_counted && item.variance !== 0);
  };

  const getSkippedItems = () => {
    return items.filter(item => item.is_skipped);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Đang tải dữ liệu...</Typography>
      </Container>
    );
  }

  if (!stocktake) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Alert severity="error">
          Không tìm thấy phiếu kiểm kho
        </Alert>
        <Button onClick={() => navigate('/stocktakes')} sx={{ mt: 2 }}>
          Quay lại danh sách
        </Button>
      </Container>
    );
  }

  const statusDisplay = stocktake.getStatusDisplay();
  const progress = stocktake.getProgressPercentage();
  const availableActions = stocktake.getAvailableActions(userRole);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/stocktakes')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {stocktake.stocktake_number}
            </Typography>
            <Chip 
              label={statusDisplay.label}
              color={statusDisplay.color}
              size="small"
            />
          </Box>
          
          <Box display="flex" gap={1}>
            <Tooltip title="In phiếu">
              <IconButton>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Xuất Excel">
              <IconButton>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            
            {availableActions.map((action) => (
              <Button
                key={action.action}
                variant={action.variant}
                color={action.color}
                startIcon={
                  action.action === 'submit' ? <SendIcon /> :
                  action.action === 'approve' ? <ApproveIcon /> :
                  action.action === 'reject' ? <RejectIcon /> :
                  <EditIcon />
                }
                onClick={() => {
                  if (action.action === 'continue') {
                    navigate(`/stocktakes/${stocktakeId}/count`);
                  } else if (action.action === 'view') {
                    // Already on detail page, no action needed
                    return;
                  } else if (action.action === 'edit') {
                    // Navigate to edit (if edit page exists) or just stay here
                    return;
                  } else {
                    // Backend actions: start, submit, approve, reject
                    handleAction(action.action);
                  }
                }}
                disabled={actionLoading}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Tiến độ
                </Typography>
                <Box display="flex" alignItems="baseline" gap={1}>
                  <Typography variant="h4">
                    {progress}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    hoàn thành
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progress}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Tổng sản phẩm
                </Typography>
                <Typography variant="h4">
                  {items.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Đã kiểm: {items.filter(item => item.is_counted).length} | 
                  Bỏ qua: {items.filter(item => item.is_skipped).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Chênh lệch
                </Typography>
                <Typography variant="h4" color={getVarianceItems().length > 0 ? 'warning.main' : 'success.main'}>
                  {getVarianceItems().length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  sản phẩm có chênh lệch
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Người tạo
                </Typography>
                <Typography variant="h6">
                  {stocktake.created_by?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(stocktake.created_at).toLocaleDateString('vi-VN')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={`Tất cả (${items.length})`} />
            <Tab label={`Chênh lệch (${getVarianceItems().length})`} />
            <Tab label={`Bỏ qua (${getSkippedItems().length})`} />
          </Tabs>
        </Paper>

        {/* Items Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Mã SP</TableCell>
                <TableCell>Tên sản phẩm</TableCell>
                <TableCell align="right">SL dự kiến</TableCell>
                <TableCell align="right">SL thực tế</TableCell>
                <TableCell align="right">Chênh lệch</TableCell>
                <TableCell align="right">Hư hỏng</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Ghi chú</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tabValue === 0 ? items : 
                tabValue === 1 ? getVarianceItems() : 
                getSkippedItems()
              ).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product?.sku || 'N/A'}</TableCell>
                  <TableCell>{item.getProductDisplayName()}</TableCell>
                  <TableCell align="right">{item.getFormattedExpectedQuantity()}</TableCell>
                  <TableCell align="right">
                    {item.is_counted ? item.getFormattedPhysicalQuantity() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {item.is_counted && (
                      <Chip 
                        label={item.getFormattedVariance()}
                        size="small"
                        color={
                          item.variance === 0 ? 'success' :
                          Math.abs(item.variance) > 20 ? 'error' : 'warning'
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {item.spoilage_count || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.getStatusDisplay()}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{item.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default StocktakeDetail;